import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { createPortal } from 'react-dom';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';

export default function FogRawGLBackground({ style }) {
  const mountRef = useRef();

  useEffect(() => {
    let renderer, scene, camera, line, glowLine, cloudGroup, animationId;
    const colorLeft = new THREE.Color('##91919');
    const colorRight = new THREE.Color('#dc255a');
    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    // Настройка рендера
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(dpr);
    renderer.setSize(width, height);
    renderer.setClearColor('#0c0c0c', 1);
    mountRef.current.appendChild(renderer.domElement);

    // Настройка сцены и камеры
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.z = 15; // Камера ближе для лучшей видимости

    const loader = new FontLoader();
    loader.load('/fonts/robot-regular.json', font => {
      // --- Облако из маленьких надписей ---
      cloudGroup = new THREE.Group();
      const instanceCount = 80;
      const radius = 10;

      for (let i = 0; i < instanceCount; i++) {
        const text = Math.random() < 0.5 ? 'yufu' : 'юфу';
        const shapes = font.generateShapes(text, 0.3, 1);
        let points = [];
        shapes.forEach(shape => {
          const shapePoints = shape.getSpacedPoints(100);
          points = points.concat(shapePoints);
        });
        const positions = new Float32Array(points.length * 3);
        for (let j = 0; j < points.length; j++) {
          positions[j * 3] = points[j].x;
          positions[j * 3 + 1] = points[j].y;
          positions[j * 3 + 2] = 0;
        }
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.center();
        // Цвет для облака (бледный gmod-magenta/gmod-pink/gmod-white)
        const colors = new Float32Array(points.length * 3);
        for (let j = 0; j < points.length; j++) {
          let color;
          if (j < points.length * 0.5) {
            color = new THREE.Color('#dc255a').lerp(new THREE.Color('#f8637e'), j / (points.length * 0.5));
          } else {
            color = new THREE.Color('#f8637e').lerp(new THREE.Color('#191919'), (j - points.length * 0.5) / (points.length * 0.5));
          }
          colors[j * 3] = color.r;
          colors[j * 3 + 1] = color.g;
          colors[j * 3 + 2] = color.b;
        }
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        const material = new THREE.LineBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: 0.3,
          linewidth: 1,
        });
        const cloudLine = new THREE.Line(geometry, material);
        // Сферическое распределение
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        const x = radius * Math.sin(phi) * Math.cos(theta);
        const y = radius * Math.sin(phi) * Math.sin(theta);
        const z = radius * Math.cos(phi);
        cloudLine.position.set(x, y, z);
        cloudLine.scale.set(0.4, 0.4, 0.4);
        cloudLine.lookAt(0, 0, 0);
        cloudGroup.add(cloudLine);
      }
      scene.add(cloudGroup);
    }, undefined, error => {
      console.error('Font loading error:', error);
    });

    let startTime = null;
    const drawDuration = 6;

    function animate() {
      const t = performance.now() * 0.001;
      if (startTime === null) startTime = t;

      // Вращение облака (только по Y, медленно)
      if (cloudGroup) {
        cloudGroup.rotation.y = t * 0.07;
        // Индивидуальное вращение каждой надписи (ускорено)
        cloudGroup.children.forEach((child, i) => {
          child.rotation.z = t * 2.5 * (0.7 + 0.3 * Math.sin(i));
          child.rotation.y = t * 2.5 * (0.5 + 0.2 * Math.cos(i));
        });
      }

      renderer.render(scene, camera);
      animationId = requestAnimationFrame(animate);
    }
    animate();

    // Обработка изменения размера окна
    function handleResize() {
      width = window.innerWidth;
      height = window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }
    window.addEventListener('resize', handleResize);

    // Очистка при размонтировании
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
      renderer.dispose();
      if (line) {
        line.geometry.dispose();
        line.material.dispose();
        scene.remove(line);
      }
      if (glowLine) {
        glowLine.geometry.dispose();
        glowLine.material.dispose();
        scene.remove(glowLine);
      }
      if (cloudGroup) {
        cloudGroup.children.forEach(child => {
          child.geometry.dispose();
          child.material.dispose();
        });
        scene.remove(cloudGroup);
      }
      if (mountRef.current && renderer.domElement.parentNode === mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  return createPortal(
    <div
      ref={mountRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        backgroundColor: '#0c0c0c',
        ...style,
      }}
    />,
    document.body
  );
}